import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * Choose which part of a picture Facebook keeps.
 *
 * Facebook crops a profile picture to a square and a cover to a wide strip. Up
 * to now you found out what had been cut off after it was already live; here
 * the frame has the same shape Facebook will use, so what you see is what the
 * Page gets.
 *
 * It also fixes a second problem quietly: a photo straight from a phone is
 * eight or ten megabytes, and what Facebook needs is a fraction of that. The
 * crop is drawn to a canvas at a sane size, so what leaves the browser is
 * usually under a megabyte.
 *
 * No library. A canvas, a pointer, and two numbers — scale and offset.
 */

/** What Facebook actually shows, so the frame does not lie about the crop. */
const SHAPES = {
  picture: { aspect: 1, width: 720 },
  cover: { aspect: 820 / 312, width: 1640 },
} as const;

export function ImageCropper({
  file,
  kind,
  onCancel,
  onDone,
}: {
  file: File;
  kind: 'picture' | 'cover';
  onCancel: () => void;
  onDone: (cropped: File) => void;
}) {
  const { t } = useI18n();
  const shape = SHAPES[kind];

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  const frameRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const element = new Image();
    let cancelled = false;

    element.onload = () => {
      if (cancelled) return;
      setImage(element);
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
    element.src = url;

    return () => {
      /*
       * Revoked on the way out, not on load: the same URL is what the visible
       * <img> renders from, and releasing it early would leave a blank frame.
       * `cancelled` stops a decode that finishes after this file was replaced
       * from overwriting the newer one.
       */
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  /*
   * Measured after layout rather than read during render: on the first pass the
   * ref is still empty and every size would come from the fallback, which is
   * how a crop ends up scaled to a number nobody chose.
   */
  const [frameWidth, setFrameWidth] = useState(320);

  useEffect(() => {
    const element = frameRef.current;
    if (!element) return;

    const measure = () => setFrameWidth(element.clientWidth || 320);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const frameHeight = frameWidth / shape.aspect;

  /*
   * The smallest scale that still covers the frame. Anything below it would
   * leave a gap, and a gap in a profile picture is not a crop — it is a mistake
   * Facebook fills with white.
   */
  const minScale = image
    ? Math.max(frameWidth / image.naturalWidth, frameHeight / image.naturalHeight)
    : 1;
  const drawScale = minScale * scale;

  const clamp = (next: { x: number; y: number }) => {
    if (!image) return next;
    const drawnWidth = image.naturalWidth * drawScale;
    const drawnHeight = image.naturalHeight * drawScale;
    const maxX = Math.max(0, (drawnWidth - frameWidth) / 2);
    const maxY = Math.max(0, (drawnHeight - frameHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  };

  const crop = async () => {
    if (!image) return;
    setBusy(true);

    const canvas = document.createElement('canvas');
    canvas.width = shape.width;
    canvas.height = Math.round(shape.width / shape.aspect);

    const context = canvas.getContext('2d');
    if (!context) {
      setBusy(false);
      return;
    }

    // The frame is a window onto the image; the canvas repeats that window at
    // the size Facebook wants.
    const ratio = canvas.width / frameWidth;
    const drawnWidth = image.naturalWidth * drawScale * ratio;
    const drawnHeight = image.naturalHeight * drawScale * ratio;
    const x = (canvas.width - drawnWidth) / 2 + offset.x * ratio;
    const y = (canvas.height - drawnHeight) / 2 + offset.y * ratio;

    context.drawImage(image, x, y, drawnWidth, drawnHeight);

    const blob = await new Promise<Blob | null>((resolve) =>
      // JPEG rather than PNG: a photograph as PNG is several times the size for
      // no visible gain, and this is always a photograph.
      canvas.toBlob(resolve, 'image/jpeg', 0.92),
    );

    setBusy(false);
    if (blob) onDone(new File([blob], `${kind}.jpg`, { type: 'image/jpeg' }));
  };

  return (
    <div className="cropper">
      <div
        className="cropper-frame"
        ref={frameRef}
        style={{ aspectRatio: String(shape.aspect) }}
        onPointerDown={(event) => {
          dragging.current = {
            x: event.clientX - offset.x,
            y: event.clientY - offset.y,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!dragging.current) return;
          setOffset(
            clamp({
              x: event.clientX - dragging.current.x,
              y: event.clientY - dragging.current.y,
            }),
          );
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
      >
        {image && (
          <img
            src={image.src}
            alt=""
            draggable={false}
            style={{
              width: image.naturalWidth * drawScale,
              height: image.naturalHeight * drawScale,
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        )}
      </div>

      <label htmlFor="crop-zoom" className="muted small">
        {t('cropper.zoom')}
      </label>
      <input
        id="crop-zoom"
        type="range"
        min="1"
        max="3"
        step="0.01"
        value={scale}
        onChange={(event) => {
          setScale(Number(event.target.value));
          // Re-clamped, or zooming out would leave the image parked off-centre
          // with a gap along one edge.
          setOffset((current) => clamp(current));
        }}
      />

      <div className="cropper-actions">
        <button type="button" className="ghost small" onClick={onCancel}>
          {t('cropper.cancel')}
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => void crop()}
          disabled={busy || !image}
        >
          <Icon.check />
          {busy ? t('cropper.working') : t('cropper.use')}
        </button>
      </div>

      <p className="muted small">{t(`cropper.hint.${kind}` as never)}</p>
    </div>
  );
}
